import { track } from "@/utils/events/tracker";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { forceReloadIFrame, setDevServerUrl } from "@/store/iframeSlice";
import { toast } from "sonner";
import {
  getAxiosErorrMessage,
  getDevServerUrl,
  getUnexpiredToken,
  logAxiosError,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
} from "./utils";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { fetchGitStatus } from "@/store/gitStatusSlice";
import { useChangeLedgerStore } from "@/store/zustand/changeLedger";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLocalMode from "@/hooks/useLocalMode";
import { Canvas } from "./types";
import {
  DevErrorJson,
  RunningServerCommand,
  ServerCommandType,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { useEffect, useRef } from "react";
import { stripAnsiFromTerminalOutput } from "./console";
import { useSession } from "@clerk/clerk-react";

export const startDevServerAndReportErrors = async ({
  addTerminalErrors,
  canvasId,
  extraEnvVariables,
  abortController,
  token,
  restart,
}: {
  addTerminalErrors?: (errors: DevErrorJson[]) => void;
  canvasId: string;
  extraEnvVariables?: {
    [key: string]: string;
  };
  abortController?: AbortController;
  token: string;
  restart?: boolean;
}): Promise<{
  success: boolean;
  error?: Error;
  errorConsoleOutput?: string;
}> => {
  try {
    const response = await axios.post(
      `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/devServer/startDevServer`,
      {
        restart: restart,
        extraEnvVariables,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: abortController?.signal,
      }
    );

    if (
      response.data.error &&
      response.data.errorConsoleOutput &&
      addTerminalErrors
    ) {
      addTerminalErrors([
        {
          message: stripAnsiFromTerminalOutput(
            response.data.errorConsoleOutput
          ),
          filename: "Restarting dev server",
          stack: "",
          count: 1,
        },
      ]);
    }

    return response.data;
  } catch (error) {
    return {
      success: false,
      error: error,
    };
  }
};

export interface StartServerInterface {
  extraEnvVariables?: {
    [key: string]: string;
  };
  abortController?: AbortController;
  forceRestart?: boolean;

  // If we know the container is running for sure, don't check the status to relaunch
  doNotCheckContainer?: boolean;
  doNotReloadCodebase?: boolean;
}

export const useNewRunningServerCommand = () => {
  const runningServerCommands = useDevServerStore(
    (state) => state.runningServerCommands
  );
  const setRunningServerCommands = useDevServerStore(
    (state) => state.setRunningServerCommands
  );

  const addRunningServerCommand = (command: RunningServerCommand) => {
    // If there are other running server commands, abort them
    runningServerCommands.forEach((cmd) => {
      cmd.abortController?.abort();
    });

    // If this running server command doesn't have an abort controller, create one
    if (!command.abortController) {
      command.abortController = new AbortController();
    }

    setRunningServerCommands([command]);
  };

  const addRunningServerCommandRef = useRef(addRunningServerCommand);
  useEffect(() => {
    addRunningServerCommandRef.current = addRunningServerCommand;
  }, [addRunningServerCommand]);

  const removeRunningServerCommand = (command: RunningServerCommand) => {
    setRunningServerCommands(
      runningServerCommands.filter((cmd) => cmd !== command)
    );
  };

  const removeRunningServerCommandRef = useRef(removeRunningServerCommand);
  useEffect(() => {
    removeRunningServerCommandRef.current = removeRunningServerCommand;
  }, [removeRunningServerCommand]);

  return {
    addRunningServerCommandRef,
    removeRunningServerCommandRef,
  };
};

export const useStartServer = () => {
  const canvasId = useCanvasId();

  const project = useActiveProject();
  const mainCanvas = useActiveMainCanvas();

  const { session } = useSession();

  const dispatch = useDispatch();

  const setProjectLaunchStatus = useCodebaseStore(
    (state) => state.setProjectLaunchStatus
  );

  const { supabaseClient, triggerRefetchProjectLoadingProgress } =
    useSupabase();

  const launchContainer = useLaunchContainer();

  const loadCodebase = useLoadCodebase();

  const devServerStatus = useSelector(
    (state: any) => state.codebase.devServerStatus
  );

  const devServerStatusRef = useRef(devServerStatus);
  useEffect(() => {
    devServerStatusRef.current = devServerStatus;
  }, [devServerStatus]);

  const { addRunningServerCommandRef, removeRunningServerCommandRef } =
    useNewRunningServerCommand();
  const addTerminalErrors = useDevServerStore(
    (state) => state.addTerminalErrors
  );

  return async (
    inp?: StartServerInterface
  ): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (!supabaseClient) {
      return {
        success: false,
        error: "No supabase client",
      };
    }

    const {
      extraEnvVariables,
      abortController,
      forceRestart,
      doNotCheckContainer,
      doNotReloadCodebase,
    } = inp || {};

    const containerRunning =
      devServerStatusRef.current?.containerStatus?.running;

    // Gets set back on the backend when done
    const relaunchingContainer = !doNotCheckContainer && !containerRunning;
    const updateDict = {
      started_dev_server: false,
      dev_server_responding: false,
      node_modules_installed: false,
      last_updated: new Date().toISOString(),
    };

    if (!doNotReloadCodebase) {
      updateDict["files_loaded"] = false;
    }

    if (relaunchingContainer) {
      updateDict["launched_container"] = false;
      updateDict["stopped_container"] = false;
    }

    await supabaseClient
      .from("project_loading_progress")
      .update(updateDict)
      .eq("canvas_id", canvasId);

    triggerRefetchProjectLoadingProgress();

    if (!doNotReloadCodebase) {
      await loadCodebase(false, true, abortController);
    }

    if (abortController?.signal.aborted) {
      return {
        success: false,
        error: "Operation aborted by user",
      };
    }

    // If container isn't running, launch it
    if (!containerRunning && !doNotCheckContainer) {
      const { success, error } = await launchContainer({
        abortController,
        forceRelaunch: true,
      });

      if (!success) {
        return {
          success: false,
          error: error || "Error launching container",
        };
      }
    }

    const command = {
      commandType: ServerCommandType.START_SERVER,
      startTime: new Date(),
      abortController: abortController,
    };

    addRunningServerCommandRef.current(command);

    if (!mainCanvas) {
      return {
        success: false,
        error: "Canvas not found",
      };
    }

    const startTime = performance.now();

    track("RESTART");

    const token = await getUnexpiredToken(session);
    setProjectLaunchStatus("starting");

    const { error } = await startDevServerAndReportErrors({
      canvasId,
      extraEnvVariables,
      token: token!,
      addTerminalErrors,
      abortController: command.abortController,
      restart: forceRestart,
    });

    removeRunningServerCommandRef.current(command);

    triggerRefetchProjectLoadingProgress();

    const { data: freshData } = await supabaseClient
      .from("canvases")
      .select(
        `
        *, canvas_machine_mapping(machine:deployment_machines(new_dev_server_suffix,is_new_active,dev_server_suffix))
      `
      )
      .eq("id", canvasId)
      .single();

    const freshCanvas = freshData as Canvas & {
      canvas_machine_mapping: {
        machine: {
          new_dev_server_suffix: string;
          is_new_active: boolean;
          dev_server_suffix: string;
        };
      };
    };

    // Set the project launch status to success even if there is an error to close the modal
    const endTime = performance.now();

    const machine = freshCanvas?.canvas_machine_mapping?.machine;
    const devServerSuffix = machine?.is_new_active
      ? machine?.new_dev_server_suffix
      : machine?.dev_server_suffix;

    // Project successfully loaded, record results
    const devServerUrl = getDevServerUrl(freshCanvas, devServerSuffix);

    dispatch(
      setDevServerUrl({
        canvasId,
        url: devServerUrl,
      })
    );

    dispatch(forceReloadIFrame());

    if (error) {
      if (!command.abortController?.signal.aborted) {
        logAxiosError("Error restarting", error);
        toast.error("Error restarting: " + getAxiosErorrMessage(error));
        setProjectLaunchStatus("error");
      }

      return {
        success: false,
        error: getAxiosErorrMessage(error),
      };
    }

    setProjectLaunchStatus("succeeded");

    console.debug(`Restart request: ${endTime - startTime} ms`);

    return {
      success: true,
    };
  };
};

export interface RelaunchContainerInterface {
  abortController?: AbortController;
  forceRelaunch?: boolean;
}

export const useLaunchContainer = () => {
  const canvasId = useCanvasId();
  const mainCanvas = useActiveMainCanvas();

  const { session } = useSession();

  const setProjectLaunchStatus = useCodebaseStore(
    (state) => state.setProjectLaunchStatus
  );

  const { supabaseClient, triggerRefetchProjectLoadingProgress } =
    useSupabase();

  const { addRunningServerCommandRef, removeRunningServerCommandRef } =
    useNewRunningServerCommand();

  return async (
    inp?: RelaunchContainerInterface
  ): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const { abortController, forceRelaunch } = inp || {};

    if (!mainCanvas) {
      return {
        success: false,
        error: "Canvas not found",
      };
    }

    const command = {
      commandType: ServerCommandType.START_SERVER,
      startTime: new Date(),
      abortController: abortController,
    };

    addRunningServerCommandRef.current(command);

    const startTime = performance.now();

    const token = await getUnexpiredToken(session);
    setProjectLaunchStatus("launching");

    // Gets set back on the backend when done
    // @ts-expect-error
    await supabaseClient
      .from("project_loading_progress")
      .update({
        started_dev_server: false,
        dev_server_responding: false,
        node_modules_installed: false,
        launched_container: false,
        stopped_container: !Boolean(forceRelaunch),
        last_updated: new Date().toISOString(),
      })
      .eq("canvas_id", canvasId);

    triggerRefetchProjectLoadingProgress();

    // Launch the container
    let startContainerRes;
    let error;
    try {
      startContainerRes = (
        await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/devServer/runContainer`,
          {
            relaunch: Boolean(forceRelaunch),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: command.abortController?.signal,
          }
        )
      ).data;
    } catch (e) {
      error = e;
    }

    removeRunningServerCommandRef.current(command);

    triggerRefetchProjectLoadingProgress();

    if (error) {
      if (!command.abortController?.signal.aborted) {
        logAxiosError("Error relaunching", error);
        toast.error("Error relaunching: " + getAxiosErorrMessage(error));
        setProjectLaunchStatus("error");
      }

      return {
        success: false,
        error: getAxiosErorrMessage(error),
      };
    }

    console.debug(`Relaunch request: ${performance.now() - startTime} ms`);

    return {
      success: true,
    };
  };
};

export const useLoadCodebase = () => {
  const canvasId = useCanvasId();
  const { isLocalMode } = useLocalMode();

  const { session } = useSession();

  const dispatch = useDispatch<any>();

  const setAllLookups = useCodebaseStore((state) => state.setAllLookups);

  const setFlushing = useChangeLedgerStore((state) => state.setFlushing);

  const setProjectLaunchStatus = useCodebaseStore(
    (state) => state.setProjectLaunchStatus
  );

  const { supabaseClient, triggerRefetchProjectLoadingProgress } =
    useSupabase();

  return async (
    showFlush?: boolean,
    showProgress?: boolean,
    abortController?: AbortController
  ) => {
    if (!supabaseClient || !session) {
      return;
    }
    if (showFlush) {
      setFlushing(true);
    }

    if (showProgress) {
      setProjectLaunchStatus("loading");

      await supabaseClient
        .from("project_loading_progress")
        .update({
          files_loaded: false,
          last_updated: new Date().toISOString(),
        })
        .eq("canvas_id", canvasId);

      triggerRefetchProjectLoadingProgress();
    }

    const supabaseJWT = await getUnexpiredToken(session);

    let loadResponse;
    try {
      loadResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/load`,
        {
          branch: null,
          isLocalMode,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
          signal: abortController?.signal,
        }
      );

      if (loadResponse.status > 400) {
        throw new Error(loadResponse.data);
      }

      setAllLookups({
        lookups: loadResponse.data,
        canvasId,
      });
      dispatch(
        fetchGitStatus({
          session,
          canvasId,
        })
      );
    } catch (e) {
      if (abortController?.signal.aborted) {
        return;
      }

      logAxiosError("Error loading project", e);
      toast.error("Error loading project: " + getAxiosErorrMessage(e));
    }

    triggerRefetchProjectLoadingProgress();

    if (showFlush) {
      setFlushing(false);
    }

    if (showProgress) {
      setProjectLaunchStatus("succeeded");
    }

    return loadResponse;
  };
};

export interface RunTerminalCommandInterface {
  command: string;
  abortController?: AbortController;
  extraEnvVariables?: {
    [key: string]: string;
  };
}

export const useRunTerminalCommand = () => {
  const canvasId = useCanvasId();
  const addTerminalErrors = useDevServerStore(
    (state) => state.addTerminalErrors
  );

  const { session } = useSession();

  const devServerStatus = useSelector(
    (state: any) => state.codebase.devServerStatus
  );

  const devServerStatusRef = useRef(devServerStatus);
  useEffect(() => {
    devServerStatusRef.current = devServerStatus;
  }, [devServerStatus]);

  const launchContainer = useLaunchContainer();
  const startServer = useStartServer();

  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );
  const projectLaunchStatusRef = useRef(projectLaunchStatus);
  useEffect(() => {
    projectLaunchStatusRef.current = projectLaunchStatus;
  }, [projectLaunchStatus]);

  return async (
    inp: RunTerminalCommandInterface
  ): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const { command, abortController, extraEnvVariables } = inp || {};

    const supabaseJWT = await getUnexpiredToken(session);

    if (!devServerStatusRef.current?.containerStatus?.running) {
      // Launch the container
      const { success, error } = await launchContainer({
        abortController,
      });

      if (!success) {
        return {
          success: false,
          error: error || "Error relaunching container",
        };
      }

      // Also trigger starting the dev server
      await startServer({
        forceRestart: true,
        abortController,
      });
    }

    try {
      const res = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/devServer/runTerminalCommand`,
        {
          command,
          extraEnvVariables,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
          signal: abortController?.signal,
        }
      );

      if (res.data.error && res.data.errorConsoleOutput) {
        addTerminalErrors([
          {
            message: res.data.errorConsoleOutput,
            filename: "Terminal",
            stack: "",
            count: 1,
          },
        ]);
      }

      return {
        success: res.data.success,
        error: res.data.error,
      };
    } catch (error) {
      if (abortController?.signal.aborted) {
        return { success: false, error: "Operation aborted by user" };
      }

      logAxiosError("Error running terminal command", error);
      toast.error(
        "Error running terminal command: " + getAxiosErorrMessage(error)
      );
      return {
        success: false,
        error: error.response?.data?.error || "Failed to run terminal command",
      };
    }
  };
};
